import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import { API_URL } from "../../../config";
import "./Proposal.css";

const initialInsights = {
  score: 0,
  summary: "",
  risks: [],
  checklist: [],
  metrics: {},
};

function Proposal() {
  const proposalRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const clientData = location.state?.client;

  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [categories] = useState([
    { value: "Laravel", label: "Laravel" },
    { value: "Vuejs", label: "Vuejs" },
    { value: "React", label: "React" },
    { value: "Zend", label: "Zend" },
    { value: "CakePhp", label: "CakePhp" },
  ]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [insights, setInsights] = useState(initialInsights);
  const [analyzing, setAnalyzing] = useState(false);

  const [proposal, setProposal] = useState({
    title: "",
    services: [],
    description: "",
    companyDescription: "",
    category: [],
    terms: "",
    file: null,
  });

  useEffect(() => {
    if (!clientData?._id) {
      alert("Client data missing! Go back and select a client.");
      navigate(-1);
    }
  }, [clientData, navigate]);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/getServices`)
      .then((res) => {
        const options = res.data.map((service) => ({
          value: service._id,
          label: service.serviceName,
          price: service.servicePrice,
        }));
        setServices(options);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (clientData?.service) {
      const leadService = {
        id: clientData.service._id,
        name: clientData.service.serviceName,
        price: clientData.project_price || clientData.service.servicePrice || 0,
      };
      setProposal((prev) => ({
        ...prev,
        services: [leadService],
      }));

      setSelectedServices([
        {
          value: clientData.service._id,
          label: clientData.service.serviceName,
          price: clientData.project_price || clientData.service.servicePrice || 0,
        },
      ]);
    }
  }, [clientData]);

  useEffect(() => {
    setProposal((prev) => ({
      ...prev,
      category: selectedCategories.map((c) => c.label),
    }));
  }, [selectedCategories]);

  useEffect(() => {
    const total = proposal.services.reduce((sum, s) => sum + Number(s.price || 0), 0);
    setTotalPrice(total);
  }, [proposal.services]);

  const handleProposalChange = (e) => {
    setProposal({ ...proposal, [e.target.name]: e.target.value });
  };

  const analyzeDraft = async (draft) => {
    try {
      setAnalyzing(true);
      const res = await axios.post(`${API_URL}/api/proposals/analyze`, {
        title: draft.title,
        services: draft.services,
        description: draft.description,
        category: draft.category,
        terms: draft.terms,
      });
      setInsights(res?.data?.insights || initialInsights);
      return res?.data?.insights;
    } catch (err) {
      console.error("Error analyzing proposal:", err?.response?.data || err);
      return null;
    } finally {
      setAnalyzing(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      analyzeDraft(proposal);
    }, 350);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.title, proposal.description, proposal.terms, proposal.category, proposal.services]);

  const handleSaveProposal = async (isSend = true) => {
    if (!clientData?._id) return;
    setLoading(true);
    try {
      const latestInsights = await analyzeDraft(proposal);
      const formData = new FormData();
      formData.append("clientId", clientData._id);
      formData.append("title", proposal.title);
      formData.append("services", JSON.stringify(proposal.services));
      formData.append("description", proposal.description);
      formData.append("companyDescription", proposal.companyDescription || "");
      formData.append("category", JSON.stringify(proposal.category));
      formData.append("terms", proposal.terms);
      formData.append("totalPrice", totalPrice);
      formData.append("clientName", clientData.leadName || clientData.ename);
      formData.append("clientEmail", clientData.emailId || clientData.personal_email);
      formData.append("clientPhone", clientData.phoneNo || "");
      formData.append("sendEmail", isSend);

      if (proposal.file) formData.append("attachments", proposal.file);

      const res = await axios.post(`${API_URL}/api/proposals`, formData);
      const savedInsights = res?.data?.insights || latestInsights;
      if (savedInsights) setInsights(savedInsights);
      const highRiskCount = (savedInsights?.risks || []).filter((r) => r.severity === "high").length;
      const warningNote = highRiskCount > 0 ? `\n\nAI check: ${highRiskCount} high-risk item(s) detected.` : "";
      alert((res?.data?.message || "Proposal saved successfully!") + warningNote);
      setShowPreview(false);
      navigate(-1);
    } catch (err) {
      console.error("Error saving proposal:", err.response?.data || err);
      alert(err?.response?.data?.message || "Failed to save proposal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4 proposal-page">
      <h2 className="text-center mb-4 proposal-title">Create Proposal</h2>

      {!showPreview ? (
        <div className="card p-3 shadow mb-4 proposal-card">
          <input
            type="text"
            name="title"
            placeholder="Proposal Title"
            className="form-control mb-3"
            value={proposal.title}
            onChange={handleProposalChange}
          />

          <CreatableSelect
            isMulti
            classNamePrefix="proposal-select"
            options={services}
            value={selectedServices}
            onChange={(selected) => {
              const mapped =
                selected?.map((s) => {
                  const existing = proposal.services.find((ps) => ps.id === s.value);
                  return {
                    id: s.value,
                    name: s.label,
                    price: existing ? existing.price : s.price || 0,
                  };
                }) || [];
              setSelectedServices(selected);
              setProposal({ ...proposal, services: mapped });
            }}
            placeholder="Select or add services"
          />

          {proposal.services.length > 0 && (
            <div className="mt-3">
              <h6>Edit Service Prices:</h6>
              {proposal.services.map((service, index) => (
                <div
                  key={service.id}
                  className="d-flex align-items-center mb-2"
                  style={{ gap: "10px" }}
                >
                  <strong style={{ width: "150px" }}>{service.name}</strong>
                  <input
                    type="number"
                    min="0"
                    className="form-control"
                    style={{ maxWidth: "200px" }}
                    value={service.price}
                    onChange={(e) => {
                      const updated = [...proposal.services];
                      updated[index].price = e.target.value;
                      setProposal({ ...proposal, services: updated });
                    }}
                  />
                </div>
              ))}

              <div className="text-end mt-2">
                <strong>Total Price: Rs {totalPrice}</strong>
              </div>
            </div>
          )}

          <textarea
            name="description"
            placeholder="Project Description"
            className="form-control mb-3"
            rows={3}
            value={proposal.description}
            onChange={handleProposalChange}
          />

          <textarea
            name="companyDescription"
            placeholder="Company Description"
            className="form-control mb-3"
            rows={3}
            value={proposal.companyDescription}
            onChange={handleProposalChange}
          />

          <CreatableSelect
            isMulti
            classNamePrefix="proposal-select"
            options={categories}
            value={selectedCategories}
            onChange={(newValue) => setSelectedCategories(newValue || [])}
            placeholder="Select or type categories"
            className="mb-3"
          />

          <textarea
            name="terms"
            placeholder="Terms & Conditions"
            className="form-control mb-3"
            rows={3}
            value={proposal.terms}
            onChange={handleProposalChange}
          />

          <label>File (Optional)</label>
          <input
            type="file"
            name="attachments"
            onChange={(e) => setProposal({ ...proposal, file: e.target.files[0] })}
            className="form-control mb-3"
          />

          <div className="mb-3 p-3 border rounded bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Proposal AI Insights</h6>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => analyzeDraft(proposal)}
                disabled={analyzing}
              >
                {analyzing ? "Analyzing..." : "Recheck"}
              </button>
            </div>

            <div className="mt-2">
              <strong>Completeness Score:</strong> {insights.score || 0}/100
            </div>
            {insights.summary ? <div className="mt-1">{insights.summary}</div> : null}

            {insights.risks?.length > 0 ? (
              <ul className="mt-2 mb-0">
                {insights.risks.map((risk) => (
                  <li key={risk.code}>
                    <strong>{risk.severity?.toUpperCase()}:</strong> {risk.message} {risk.suggestion}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-success mt-2 mb-0">No major risks detected.</div>
            )}
          </div>

          <div className="text-center mt-3">
            <button
              className="btn btn-info px-4 me-3 proposal-admin-btn"
              onClick={() => handleSaveProposal(false)}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Proposal"}
            </button>

            <button className="btn btn-primary px-4 me-3 proposal-admin-btn" onClick={() => setShowPreview(true)}>
              Preview & Send
            </button>

            <button
              type="button"
              className="btn btn-secondary px-4"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
          </div>
        </div>
      ) : (
        <div className="card p-4 shadow mb-4 proposal-card">
          <div ref={proposalRef}>
            <h2>{proposal.title}</h2>
            <p>
              <strong>Client:</strong> {clientData.leadName}
            </p>
            <p>
              <strong>Email:</strong> {clientData.emailId}
            </p>
            <p>
              <strong>Phone:</strong> {clientData.phoneNo}
            </p>

            <h5>Services:</h5>
            {proposal.services.map((s) => (
              <p key={s.id}>
                {s.name} - Rs {s.price}
              </p>
            ))}

            <p>
              <strong>Total Price:</strong> Rs {totalPrice}
            </p>

            <p>
              <strong>Project Description:</strong> {proposal.description}
            </p>
            <p>
              <strong>Company Description:</strong> {proposal.companyDescription}
            </p>
            <p>
              <strong>Categories:</strong> {proposal.category.join(", ")}
            </p>
            <p>
              <strong>Terms:</strong> {proposal.terms}
            </p>

            <div className="mt-3 p-3 border rounded bg-light">
              <strong>AI Completeness Score:</strong> {insights.score || 0}/100
              {insights.risks?.length > 0 ? (
                <ul className="mt-2 mb-0">
                  {insights.risks.map((risk) => (
                    <li key={risk.code}>
                      <strong>{risk.severity?.toUpperCase()}:</strong> {risk.message}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 mb-0 text-success">No major risk found in terms and scope.</p>
              )}
            </div>
          </div>

          <div className="text-center mt-3">
            <button
              className="btn btn-info px-4 proposal-admin-btn"
              onClick={() => handleSaveProposal(false)}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Proposal"}
            </button>

            <button
              className="btn btn-success px-4 ms-3 proposal-admin-btn"
              onClick={() => handleSaveProposal(true)}
              disabled={loading}
            >
              {loading ? "Sending..." : "Save & Send"}
            </button>

            <button
              className="btn btn-secondary px-4 ms-3"
              onClick={() => setShowPreview(false)}
            >
              Back to Edit
            </button>

            <button
              type="button"
              className="btn btn-outline-dark px-4 ms-3"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Proposal;
